'use client'

import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation';
import Link from 'next/link'

interface NavLink {
    _uid: string
    label: string
    link: {
        cached_url?: string
        url?: string
        linktype?: string
    }
    children?: NavLink[]
}

interface NavProps {
    navItems: NavLink[]
}

function getHref(link: NavLink['link']): string {
    if (link.linktype === 'url') {
        return link.url || '/'
    }
    return `/${link.cached_url || ''}`
}

export default function Nav({ navItems }: NavProps) {
    const [sticky, setSticky] = useState(false)
    const [mobileOpen, setMobileOpen] = useState(false)
    const [openFolder, setOpenFolder] = useState<string | null>(null)
    const [mobileOpenFolder, setMobileOpenFolder] = useState<string | null>(null)
    const pathname = usePathname()

    function isActive(link: NavLink['link']) {
        const href = getHref(link)
        const normalizedHref = href === '/home' ? '/' : href

        if (normalizedHref === '/') {
            return pathname === '/'
        }

        return pathname.endsWith(normalizedHref)
    }

    function hasActiveChild(item: NavLink) {
    return item.children?.some((child) => isActive(child.link))
    }

    useEffect(() => {
        const onScroll = () => setSticky(window.scrollY > 50)
        // Check initial scroll position immediately
        onScroll()
        window.addEventListener('scroll', onScroll)
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : ''
        return () => { document.body.style.overflow = '' }
    }, [mobileOpen])

    // Close mobile menu on resize to desktop
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 855 && mobileOpen) {
                setMobileOpen(false)
                setMobileOpenFolder(null)
            }
        }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [mobileOpen])

    return (
        <>
            <header
                className={`
                    fixed w-full max-w-full top-0 left-0 z-40
                    transition-[background-color,box-shadow] duration-300 ease-in-out
                    ${sticky ? 'bg-white shadow-md' : 'bg-transparent shadow-none'}
                `}
            >
                <nav className="max-w-[1200px] mx-auto px-5 md:px-10 py-4 md:py-8 flex justify-between items-center">
                    <Link href="/" onClick={() => setMobileOpen(false)}>
                        <span className="font-bold text-[20px] md:text-[18px] lg:text-xl mr-10">Foma-Galvanik&nbsp;AG</span>
                    </Link>

                    {/* Desktop Links */}
                    <ul className="hidden md:flex md:gap-4 lg:gap-6 xl:gap-8 items-center">
                        {navItems.map((item) => (
                            <li
                                key={item._uid}
                                className="relative group"
                                onMouseEnter={() => item.children?.length && setOpenFolder(item._uid)}
                                onMouseLeave={() => setOpenFolder(null)}
                            >
                                {item.children && item.children.length > 0 ? (
                                    <>
                                        <span className={`${hasActiveChild(item) ? (sticky ? 'text-primary!' : 'text-background!') : 'text-foreground'} font-semibold transition cursor-pointer whitespace-nowrap text-[14px] lg:text-[16px] xl:text-[20px]  ${sticky ? 'hover:text-primary' : 'hover:text-background'}`}>
                                            {item.label}
                                        </span>
                                        <ul
                                            className={`absolute top-full left-0 mt-2 bg-background shadow-lg rounded-md py-2 min-w-32 lg:min-w-36 xl:min-w-42 transition-all duration-200 ${
                                                openFolder === item._uid ? 'opacity-100 visible' : 'opacity-0 invisible'
                                            }`}
                                        >
                                            {item.children.map((child) => (
                                                <li key={child._uid}>
                                                    <Link
                                                        href={getHref(child.link)}
                                                        className="block px-4 py-2 text-foreground hover:text-primary transition text-[14px] lg:text-[16px] xl:text-[20px]"
                                                        onClick={() => setOpenFolder(null)}
                                                    >
                                                        {child.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                ) : (
                                    <Link
                                        href={getHref(item.link)}
                                        className={`
                                            text-foreground font-semibold transition whitespace-nowrap text-[14px] lg:text-[16px] xl:text-[20px]
                                            ${isActive(item.link) ? (sticky ? 'text-primary!' : 'text-background!') : ''}
                                            ${sticky ? 'hover:text-primary' : 'hover:text-background'}
                                        `}>
                                        {item.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>

                    {/* Mobile Hamburger */}
                    <button
                        className="md:hidden focus:outline-none cursor-pointer"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        aria-label={mobileOpen ? 'Menü schliessen' : 'Menü öffnen'}
                        aria-expanded={mobileOpen}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        </svg>
                    </button>
                </nav>
            </header>

            {/* Mobile Menu */}
            <div
                className={`fixed inset-0 z-50 bg-white transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}
            >
                {/* Mobile Menu Header */}
                {/* Scrollable menu content */}
                <div className="overflow-y-auto flex-1">
                    <div className="flex items-center justify-between p-5 border-b-4 border-black bg-white z-50">
                        <span className="font-bold text-l">Menü</span>
                        <button
                            className="w-6 h-6 flex items-center justify-end rounded-full transition hover:opacity-70 transition cursor-pointer"
                            onClick={() => setMobileOpen(false)}
                            aria-label="Menü schliessen"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <ul className="flex flex-col p-5 md:p-10 gap-6">
                        {navItems.map((item) => (
                            <li key={item._uid}>
                                {item.children && item.children.length > 0 ? (
                                    <div>
                                        <button
                                            className={`flex items-center justify-between w-full font-bold text-l md:text-2xl hover:text-primary transition cursor-pointer ${hasActiveChild(item) ? 'text-primary' : 'text-foreground'}`}
                                            onClick={() => setMobileOpenFolder(mobileOpenFolder === item._uid ? null : item._uid)}
                                        >
                                            <span>{item.label}</span>
                                            <svg
                                                className="w-5 h-5 transition-transform duration-200"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                style={{ transform: mobileOpenFolder === item._uid ? 'rotate(45deg)' : 'rotate(0deg)' }}
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                        </button>
                                        <ul
                                            className={`pl-4 flex flex-col gap-3 overflow-hidden transition-all duration-300 ${
                                                mobileOpenFolder === item._uid ? 'max-h-96 mt-3 opacity-100' : 'max-h-0 opacity-0'
                                            }`}
                                        >
                                            {item.children.map((child) => (
                                                <li key={child._uid}>
                                                    <Link
                                                        href={getHref(child.link)}
                                                        className={`block text-l hover:text-primary transition ${isActive(child.link) ? 'text-primary' : ''}`}
                                                        onClick={() => setMobileOpen(false)}
                                                    >
                                                        {child.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <Link
                                        href={getHref(item.link)}
                                        className={`block font-bold text-l md:text-2xl hover:text-primary transition ${isActive(item.link) ? 'text-primary' : 'text-foreground'}`}
                                        onClick={() => setMobileOpen(false)}
                                    >
                                        {item.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </>
    )
}